// Query strings are reported to analytics by design (see CLAUDE.md — the
// page_view contract includes pathname and query string). Some of our own URLs
// carry credentials in that query string, though, and a page_view is sent to a
// third party and retained there:
//
//   /reset-password?token=<raw>   single-use, 30-minute account-takeover token
//   /auth/callback?code=…&state=… OAuth authorization code
//
// GA4's automatic query redaction does not apply to an explicitly-supplied
// page_path, so the value must be sanitized before it leaves the browser. Param
// names are redacted rather than the whole query string, so UTM and pagination
// facets keep working.

const SENSITIVE_PARAMS = new Set([
  "token",
  "access_token",
  "refresh_token",
  "id_token",
  "session_token",
  "interview_session_token",
  "code",
  "state",
  "jwt",
  "key",
  "api_key",
  "apikey",
  "secret",
  "client_secret",
  "signature",
  "sig",
  "auth",
  "authorization",
  "password",
  "pwd",
  "email",
  "reschedule",
  "handoff_code",
  "handoffcode",
  "code_verifier",
  "code_challenge",
  "token_hash",
  "desktop_nonce",
  "desktop_device_label",
  "x-amz-signature",
  "x-amz-credential",
  "x-amz-security-token",
])

// URL-safe on purpose: this value is re-encoded by URLSearchParams.toString(),
// so angle brackets would surface in GA reports as %3Credacted%3E.
export const REDACTED = "redacted"

function redactSearchParams(params: URLSearchParams, depth: number): boolean {
  let changed = false
  for (const key of Array.from(new Set(params.keys()))) {
    let decodedKey = key
    for (let i = 0; i < 4 && decodedKey.includes("%"); i++) {
      try {
        decodedKey = decodeURIComponent(decodedKey)
      } catch {
        break
      }
    }
    if (SENSITIVE_PARAMS.has(decodedKey.toLowerCase())) {
      params.set(key, REDACTED)
      changed = true
      continue
    }
    const values = params.getAll(key)
    const safe = values.map((value) => {
      let decoded = value
      for (let i = 0; i < 4 && decoded.includes("%"); i++) {
        try {
          decoded = decodeURIComponent(decoded)
        } catch {
          break
        }
      }
      // Redirect URLs can themselves contain reset tokens or OAuth codes.
      // At the recursion bound discard the value instead of leaking a deeper URL.
      if (!/[?#]/.test(decoded) && !/^(?:\/|https?:\/\/)/i.test(decoded)) return value
      if (depth >= 4) return REDACTED
      const sanitized = /^https?:\/\//i.test(decoded)
        ? redactUrl(decoded, depth + 1)
        : redactPath(decoded, depth + 1)
      return sanitized === decoded ? value : sanitized
    })
    if (safe.some((value, i) => value !== values[i])) {
      params.delete(key)
      safe.forEach((value) => params.append(key, value))
      changed = true
    }
  }
  return changed
}

/**
 * Redact credential-bearing query params from a `pathname[?query]` string.
 * Returns the input unchanged when there is nothing sensitive to remove.
 */
export function redactAnalyticsPath(pathAndQuery: string): string {
  return redactPath(pathAndQuery, 0)
}

function redactPath(value: string, depth: number): string {
  // Fragments are not part of the page-view contract and can contain tokens.
  const withoutHash = value.split("#")[0]
  const queryStart = withoutHash.indexOf("?")
  const rawPath = queryStart === -1 ? withoutHash : withoutHash.slice(0, queryStart)
  let decodedPath = rawPath
  try {
    decodedPath = decodeURI(rawPath)
  } catch {
    /* Malformed escape: retain the path. */
  }
  const maskedPath = decodedPath.replace(
    /^(\/(?:mock-interview\/booking|api\/mock-interview\/human\/bookings)\/)[^/]+/i,
    "$1[token]"
  )
  const path = maskedPath === decodedPath ? rawPath : maskedPath
  if (queryStart === -1) return path
  const params = new URLSearchParams(withoutHash.slice(queryStart + 1))
  if (!redactSearchParams(params, depth)) return path + withoutHash.slice(queryStart)
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

/**
 * Redact credential-bearing query params from an absolute URL. Invalid URLs,
 * URL credentials and fragments are never reported.
 */
export function redactAnalyticsUrl(href: string): string {
  return redactUrl(href, 0)
}

function redactUrl(href: string, depth: number): string {
  try {
    const url = new URL(href)
    if (url.protocol !== "https:" && url.protocol !== "http:") return ""
    return url.origin + redactPath(url.pathname + url.search, depth)
  } catch {
    return ""
  }
}
